import { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * `primary` is the one main action on a screen (marigold); `secondary` the ordinary button; `quiet` a teal text
 * link that acts ("Mark sent", "Clear"); `destructive` always asks first (`confirmAction`); `icon` is a square
 * button for "⋯" and "×" and needs an `aria-label`. `outline`, `ghost` and `dark` are kept as aliases while pages
 * move over.
 */
type Variant = 'primary' | 'secondary' | 'quiet' | 'destructive' | 'icon' | 'outline' | 'ghost' | 'dark';

const SECONDARY = 'bg-transparent text-ink border border-line-strong hover:bg-selected';
const QUIET = 'bg-transparent text-accent border border-transparent underline underline-offset-[3px] hover:text-ink';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-ink border border-primary hover:bg-primary-hover hover:border-primary-hover',
  secondary: SECONDARY,
  outline: SECONDARY,
  dark: SECONDARY,
  icon: SECONDARY,
  quiet: QUIET,
  ghost: QUIET,
  destructive: 'bg-transparent text-warning border border-warning hover:bg-warning-tint',
};

const SIZES = {
  sm: 'min-h-[36px] px-3 py-[7px]',
  md: 'min-h-[44px] px-4 py-[11px]',
  lg: 'min-h-[48px] px-5 py-[13px]',
};

const ICON_SIZES = {
  sm: 'min-h-[36px] min-w-[36px] px-[9px] py-[7px] justify-center',
  md: 'min-h-[44px] min-w-[44px] px-3 py-[11px] justify-center',
  lg: 'min-h-[48px] min-w-[48px] px-3 py-[13px] justify-center',
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: keyof typeof SIZES;
  trailing?: ReactNode;
  block?: boolean;
}

export default function Button({
  variant = 'secondary',
  size = 'md',
  trailing,
  block = false,
  className = '',
  children,
  ...rest
}: Props) {
  const disabled =
    'disabled:text-ink-3 disabled:border-line disabled:bg-transparent disabled:no-underline disabled:hover:bg-transparent';
  return (
    <button
      className={`inline-flex items-center rounded-sm font-sans font-semibold text-[15px] leading-5 whitespace-nowrap ${disabled} ${
        trailing ? 'justify-between gap-6' : 'justify-center gap-2'
      } ${block ? 'w-full' : ''} ${VARIANTS[variant]} ${variant === 'icon' ? ICON_SIZES[size] : SIZES[size]} ${className}`}
      {...rest}
    >
      <span>{children}</span>
      {trailing}
    </button>
  );
}
